<template>
  <div class="h-screen flex flex-col">
    <ChooseReaderModal />

    <div
      v-if="loading || isEmbedPdfLoading || !engine"
      class="h-full w-full flex flex-col items-center justify-center gap-2"
    >
      <PSpinner size="lg" enabled />
      <span>Chargement du document</span>
    </div>

    <div v-else-if="error">
      <p>Une erreur s'est produite</p>
      <p>{{ error.message }}</p>
    </div>

    <EmbedPDF
      v-else
      :engine="engine"
      :plugins="plugins"
      v-slot="{ activeDocumentId, activeDocument }"
      v-on:initialized="openDocument"
    >
      <DocumentContent v-if="activeDocumentId" :document-id="activeDocumentId" v-slot="{ isLoaded }">
        <PdfHeader @close="close" :document="document!" class="shrink-0" />

        <div class="border-t border-grey-925 grow overflow-y-auto flex">
          <PdfTools @close="close" :document="document!" />
          <div class="overflow-hidden relative flex-1" :class="defaultColorClass">
            <div v-if="!isLoaded" class="h-full w-full flex flex-col items-center justify-center gap-2">
              <PSpinner size="lg" enabled />
              <span>Chargement du document...</span>
            </div>
            <template v-else>
              <SignObjectsContainer :id-doc-to-sign="document!.id" />
              <Viewport :document-id="activeDocumentId">
                <ZoomGestureWrapper :document-id="activeDocumentId">
                  <Scroller :document-id="activeDocumentId">
                    <template #default="{ page }">
                      <div
                        :style="{
                          width: `${page.rotatedWidth}px`,
                          height: `${page.rotatedHeight}px`,
                        }"
                        class="relative"
                      >
                        <!-- RenderLayer/TilingLayer paint the page's raw, un-rotated
                          bitmap regardless of /Rotate, so we CSS-rotate this
                          wrapper ourselves to match the rotatedWidth/Height slot
                          above (mirrors embedpdf's own "container" UI). -->
                        <div
                          :style="{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: `${page.width}px`,
                            height: `${page.height}px`,
                            ...getPageRotationStyle(page.pageIndex, activeDocument),
                          }"
                        >
                          <PagePointerProvider
                            class="pagePointerProvider"
                            :document-id="activeDocumentId"
                            :page-index="page.pageIndex"
                            :page-width="page.width"
                            :page-height="page.height"
                          >
                            <RenderLayer
                              :document-id="activeDocumentId"
                              :page-index="page.pageIndex"
                              :scale="1.0"
                              class="pointer-events-none"
                            />
                            <TilingLayer
                              :document-id="activeDocumentId"
                              :page-index="page.pageIndex"
                              class="pointer-events-none"
                            />

                            <SelectionLayer :document-id="activeDocumentId" :page-index="page.pageIndex" />
                            <AnnotationLayer
                              :document-id="activeDocumentId"
                              :page-index="page.pageIndex"
                              :page-width="page.width"
                              :page-height="page.height"
                            >
                              <template #selection-menu="{ rect, context }">
                                <button
                                  v-if="isOurs(context.annotation.object)"
                                  class="absolute w-4 z-20 bg-white rounded-full p-1v shadow-lg"
                                  :style="{
                                    top: `${getDeleteIconAnchor(rect, page.pageIndex, activeDocument).y}px`,
                                    left: `${getDeleteIconAnchor(rect, page.pageIndex, activeDocument).x}px`,
                                    transform: `translate(-60%, -50%) rotate(${-90 * getPageRotation(page.pageIndex, activeDocument)}deg)`,
                                  }"
                                  @click="deleteSignObj(context.annotation.object.id)"
                                  :data-sentry-id="`signPosDeleteBtn-${context.annotation.object.id}`"
                                >
                                  <OhVueIcon class="text-error" :name="deleteIcon" />
                                </button>
                              </template>
                              <template #rotation-handle>
                                <div />
                              </template>
                            </AnnotationLayer>
                          </PagePointerProvider>
                        </div>
                      </div>
                    </template>
                  </Scroller>
                </ZoomGestureWrapper>
              </Viewport>
            </template>
          </div>
        </div>
      </DocumentContent>
    </EmbedPDF>
  </div>
</template>

<script setup lang="ts">
import ChooseReaderModal from "@/app/components/ChooseReaderModal.vue";
import syncCurrentLetterFile from "@/app/composable/sync-current-letter-file.ts";
import type { LetterFileDocumentDto } from "@/app/dto/letter-file-document.dto.ts";
import { useLetterFileDocumentApi } from "@/app/http/letter-file-document.api.ts";
import { useLetterFileStore } from "@/app/stores/letter-file.store.ts";
import { PendingSignature } from "@/app/views/PdfViewer/domain/pending-signature.domain.ts";
import { SignPosition } from "@/app/views/PdfViewer/domain/sign-position.domain.ts";
import { createPluginRegistration, type DocumentState, PluginRegistry } from "@embedpdf/core";
import { EmbedPDF } from "@embedpdf/core/vue";
import { usePdfiumEngine } from "@embedpdf/engines/vue";
import { type PdfAnnotationObject } from "@embedpdf/models";
import { AnnotationLayer, AnnotationPluginPackage } from "@embedpdf/plugin-annotation/vue";
import {
  DocumentContent,
  DocumentManagerPlugin,
  DocumentManagerPluginPackage,
} from "@embedpdf/plugin-document-manager/vue";
import { HistoryPluginPackage } from "@embedpdf/plugin-history";
import { InteractionManagerPluginPackage, PagePointerProvider } from "@embedpdf/plugin-interaction-manager/vue";
import { RenderLayer, RenderPluginPackage } from "@embedpdf/plugin-render/vue";
import { Scroller, ScrollPluginPackage } from "@embedpdf/plugin-scroll/vue";
import { SelectionLayer, SelectionPluginPackage } from "@embedpdf/plugin-selection/vue";
import { TilingLayer, TilingPluginPackage } from "@embedpdf/plugin-tiling/vue";
import { Viewport, ViewportPluginPackage } from "@embedpdf/plugin-viewport/vue";
import { ZoomGestureWrapper, ZoomPluginPackage } from "@embedpdf/plugin-zoom/vue";
import { type Nullable, PSpinner, useIcons, usePastaRouter } from "@pasta/box-v2";
import { Deferred } from "@pasta/deferred";
import { OhVueIcon } from "oh-vue-icons";
import { RiDeleteBin6Line } from "oh-vue-icons/icons";
import { computed, type Ref, ref, watch } from "vue";
import { useSignEvents } from "./composable/use-sign-events.ts";
import PdfHeader from "./PdfHeader.vue";
import { ANNOT_AUTHOR_NAME } from "./domain/abstract.sign-object.domain.ts";
import { getPageRotation } from "./domain/page-rotation.util.ts";
import SignObjectsContainer from "./SignObjectsContainer.vue";
import PdfTools from "./tools/PdfTools.vue";
import { useLetterFileColor } from "../../composable/useLetterFileColor.ts";

const router = usePastaRouter();
const letterFileStore = useLetterFileStore();
const { defaultColorClass } = useLetterFileColor();

const letterFile = computed(() => {
  return letterFileStore.getCurrent();
});
const documentApi = useLetterFileDocumentApi();
const document: Ref<Nullable<LetterFileDocumentDto>> = ref(null);
const url = new Deferred<string>();

const loading = ref(true);

syncCurrentLetterFile("doc-sign");
// Watch ids, not the store object: a letter file refetch swaps the object and would re-trigger a PDF conversion
watch(
  () => [letterFile.value?.id, router.vueRouter.currentRoute.value.params.docId],
  async () => {
    if (!letterFile.value) {
      return;
    }

    const docId = router.vueRouter.currentRoute.value.params.docId as string;
    const found = (letterFile.value.folders ?? [])
      .flatMap(({ documents }) => documents ?? [])
      .find(doc => `${doc.id}` === docId);
    if (!found) {
      await router.push({ name: "error", query: { type: "DOC_NOT_FOUND" } });
    }
    document.value = found;

    try {
      const value = await documentApi.getPdfUrl(document.value!.id, letterFile.value.id);
      url.resolve(value);
    } catch (e) {
      url.reject(e);
    }

    loading.value = false;
  },
  { immediate: true }
);

const { engine, isLoading: isEmbedPdfLoading, error } = usePdfiumEngine();
const [deleteIcon] = useIcons(RiDeleteBin6Line);

const plugins = [
  // Essential plugins
  createPluginRegistration(DocumentManagerPluginPackage),
  createPluginRegistration(ViewportPluginPackage),
  createPluginRegistration(ScrollPluginPackage),
  createPluginRegistration(RenderPluginPackage, {
    withForms: true,
  }),
  createPluginRegistration(TilingPluginPackage, {
    tileSize: 768,
    overlapPx: 5,
    extraRings: 0,
  }),
  // https://github.com/embedpdf/embed-pdf-viewer/issues/271
  createPluginRegistration(ZoomPluginPackage, { minZoom: 0.25, maxZoom: 4 }),
  // Annotation plugin + dependencies
  createPluginRegistration(InteractionManagerPluginPackage),
  createPluginRegistration(SelectionPluginPackage),
  createPluginRegistration(HistoryPluginPackage),
  createPluginRegistration(AnnotationPluginPackage, {
    annotationAuthor: ANNOT_AUTHOR_NAME,
  }),
];

const signEvents = useSignEvents();

// Compensates for RenderLayer/TilingLayer ignoring /Rotate - see the wrapper
// comment in the template above.
function getPageRotationStyle(pageIndex: number, activeDocument: DocumentState | null) {
  switch (getPageRotation(pageIndex, activeDocument)) {
    case 1:
      return { transformOrigin: "top left", transform: "rotate(90deg) translateY(-100%)" };
    case 2:
      return { transformOrigin: "top left", transform: "rotate(180deg) translate(-100%, -100%)" };
    case 3:
      return { transformOrigin: "top left", transform: "rotate(270deg) translateX(-100%)" };
    default:
      return {};
  }
}

// The delete button is positioned from the annotation's raw rect, which rotates
// with the same CSS ancestor as everything else - so the raw corner landing at
// the visible top-right shifts with rotation (top-left, bottom-left, bottom-right).
function getDeleteIconAnchor(
  rect: { origin: { x: number; y: number }; size: { width: number; height: number } },
  pageIndex: number,
  activeDocument: DocumentState | null
) {
  const { origin, size } = rect;
  switch (getPageRotation(pageIndex, activeDocument)) {
    case 1:
      return { x: origin.x, y: origin.y };
    case 2:
      return { x: origin.x, y: origin.y + size.height };
    case 3:
      return { x: origin.x + size.width, y: origin.y + size.height };
    default:
      return { x: origin.x + size.width, y: origin.y };
  }
}

function close() {
  router.push({
    name: "form-update",
    params: { chrono: letterFile.value!.chronoNumber },
  });
}

async function openDocument(registry: PluginRegistry) {
  await registry
    ?.getPlugin<DocumentManagerPlugin>(DocumentManagerPlugin.id)
    ?.provides()
    ?.openDocumentUrl({ url: await url })
    .toPromise();
}

function deleteSignObj(id: string) {
  signEvents.emit("delete", id);
}

function isOurs(annot: PdfAnnotationObject) {
  return (
    annot.author === ANNOT_AUTHOR_NAME &&
    [SignPosition.ANNOTATION_TYPE, PendingSignature.ANNOTATION_TYPE].includes(annot.type)
  );
}
</script>
